import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  InputSignal,
  OnInit,
  Renderer2,
  RendererFactory2,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { catchError } from 'rxjs';

import { CmsImageData } from '@fcom/core-api/interfaces/cms-interface';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { AspectRatios, FetchPriorityEnum } from '@fcom/ui-components/components/images/enums';
import { IMAGE_WIDTHS } from '@fcom/ui-components/components/images/constants';
import { ResponsiveImageData } from '@fcom/ui-components/components/images/interfaces';

import { SrcAndWidth } from '../../../interfaces/images.interface';
import { PageMetaService } from '../../../services/page-meta/page-meta.service';
import { ImgDecoding, ImgLoading } from './lqip.interface';
import { isBlackImagePlaceholder, mapToResponsiveImageWithCustomAspectRatio } from './lqip-image.util';

@Component({
  selector: 'fcom-lqip-image',
  templateUrl: './lqip-image.component.html',
  styleUrls: ['./lqip-image.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LqipImageComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  config = inject(ConfigService);
  http: HttpClient = inject(HttpClient);
  pageMetaService = inject(PageMetaService);
  rendererFactory = inject(RendererFactory2);

  renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

  fetchPriority: InputSignal<FetchPriorityEnum> = input(FetchPriorityEnum.auto);
  alt: InputSignal<string> = input<string>('');
  imageData: InputSignal<CmsImageData> = input();
  fillParent: InputSignal<boolean> = input<boolean>(false);
  scale: InputSignal<boolean> = input<boolean>(false);
  maxWidth: InputSignal<number> = input<number>(IMAGE_WIDTHS[0]);
  isLazyLoad: InputSignal<boolean> = input<boolean>(true);
  ratio: InputSignal<keyof typeof AspectRatios> = input<keyof typeof AspectRatios>('16x9');
  responsiveImageData: Signal<ResponsiveImageData> = computed(() =>
    mapToResponsiveImageWithCustomAspectRatio(this.imageData(), AspectRatios[this.ratio()], this.maxWidth())
  );
  srcSet: Signal<SrcAndWidth[]> = computed(() => this.responsiveImageData()?.srcSet.sort((a, b) => b.width - a.width));
  srcSetAttr: Signal<string> = computed(() =>
    this.srcSet()
      ?.map(({ src, width }) => `${src} ${width}w`)
      .join(', ')
  );
  fallbackSrc: Signal<string> = computed(() => this.responsiveImageData()?.fallbackSrc);
  lqip: WritableSignal<string> = signal<string>('');
  customAspectRatio: Signal<string> = computed(() => this.responsiveImageData()?.customAspectRatio);
  aspectRatioClass: Signal<string> = computed(() =>
    this.responsiveImageData()?.hasAspectRatio ? `ratio-${this.ratio()}` : ''
  );
  loading: InputSignal<ImgLoading> = input(ImgLoading.LAZY);
  sizesFallback: Signal<string> = computed(() => {
    const cap = this.maxWidth();
    return `(max-width: ${cap}px) 100vw, ${cap}px`;
  });
  sizes: Signal<string> = computed(() => {
    return this.loading() === ImgLoading.LAZY ? `auto, ${this.sizesFallback()}` : this.sizesFallback();
  });
  decoding: InputSignal<ImgDecoding> = input(ImgDecoding.AUTO);
  originalSrc: Signal<string | null> = computed(() =>
    AspectRatios[this.ratio()] === AspectRatios.ORIGINAL ? this.responsiveImageData()?.fallbackSrc : null
  );
  enableLqip: Signal<boolean> = input(true);

  constructor() {
    effect(() => {
      if (this.fetchPriority() === FetchPriorityEnum.high) {
        const widest = this.srcSet()?.[0];
        if (widest) {
          this.pageMetaService.movePreloadResponsiveImageLinkToBody(
            this.renderer,
            widest.src,
            this.srcSetAttr(),
            this.sizesFallback()
          );
        }
      }
    });
  }
  toImagesApi(href: string): string {
    return href.replace(`${this.config.cmsUrl}/resource/blob`, `${this.config.cfg.imagesApiUrl}/images/lqip`);
  }
  ngOnInit(): void {
    if (this.enableLqip()) {
      const lqipUrl = this.toImagesApi(this.responsiveImageData()?.lqipSrc);
      this.http
        .get(lqipUrl, {
          responseType: 'text',
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            return '';
          })
        )
        .subscribe((lqip) => {
          if (!isBlackImagePlaceholder(lqip)) {
            this.lqip.set(lqip);
          }
        });
    }
    this.destroyRef.onDestroy(() => {
      if (this.fetchPriority() === FetchPriorityEnum.high) {
        this.responsiveImageData()?.srcSet.forEach((item) => {
          this.pageMetaService.removePreloadImageLinksFromBody(this.renderer, item.src);
        });
      }
    });
  }
}
